import dayjs from 'dayjs'

import type { FormField } from './AnswerFilter'
import type { FilterChipEntry } from './FilterChipBar'
import type { DateRange, FilterCategory, ProcessStageField } from './Filters'
import type { FilterState } from './useFilterState'

export interface FilterStateOptions {
  categories: FilterCategory[]
  state: FilterState
  stagesByProcess?: Record<string, ProcessStageField[]>
  formFieldsByProcess?: Record<string, FormField[]>
  /**
   * Workflow (process) locked by the page (e.g. workflow page). Its name is
   * omitted from the chip since the page already implies it; only its stage /
   * answer refinements still surface as chips.
   */
  fixedProcessId?: string
}

// ---------------------------------------------------------------------------
// buildFilterChips
// ---------------------------------------------------------------------------

/**
 * Builds a flat list of `FilterChipEntry` descriptors from the current filter
 * state.  The returned list can be passed directly to `<FilterChipBar chips={…} />`.
 */
export const buildFilterChips = ({
  categories,
  state,
  stagesByProcess = {},
  formFieldsByProcess = {},
  fixedProcessId,
}: FilterStateOptions): FilterChipEntry[] => {
  const { selections = {}, dateRanges = {}, refinements = {}, answerFilters = {} } = state
  const entries: FilterChipEntry[] = []

  for (const [catId, optionIds] of Object.entries(selections)) {
    if (!optionIds.length) continue
    const cat = categories.find((c) => c.id === catId)
    if (!cat || cat.type === 'date') continue

    let loading = false

    const resolveLabel = (oid: string): string | null => {
      if (cat.noneOption?.id === oid) return cat.noneOption.label
      const opt = cat.options?.find((o) => o.id === oid)
      if (opt) return opt.label
      for (const dd of cat.drilldowns ?? []) {
        const ddOpt = dd.options.find((o) => o.id === oid)
        if (ddOpt) return ddOpt.label
      }
      return null
    }

    const resolveTooltip = (oid: string): string | undefined => {
      const opt = cat.options?.find((o) => o.id === oid)
      return opt?.tooltip
    }

    if (cat.type === 'process') {
      const processLabels: string[] = []
      const stageLabels: string[] = []
      const answerLabels: string[] = []

      for (const processId of optionIds) {
        const processLabel = resolveLabel(processId)
        if (processLabel === null) {
          loading = true
        } else if (processId !== fixedProcessId) {
          // The page already implies the fixed workflow; omit its name.
          processLabels.push(processLabel)
        }

        const stageIds = refinements[catId]?.[processId]?.['stages'] ?? []
        if (stageIds.length) {
          const stages = stagesByProcess[processId] ?? []
          for (const sid of stageIds) {
            const stageName = stages.find((s) => s.id === sid)?.name
            if (stageName) {
              stageLabels.push(stageName)
            } else {
              loading = true
            }
          }
        }

        const appliedAFs = (answerFilters[catId]?.[processId] ?? []).filter(
          (f) => f.applied && f.fieldId,
        )
        for (const af of appliedAFs) {
          const fields = formFieldsByProcess[processId] ?? []
          const field = fields.find((ff) => ff.id === af.fieldId)
          if (!field) {
            loading = true
            continue
          }
          const isText = field.style === 'RawInputStyle' || field.style === 'LongTextInputStyle'
          const value = isText ? `"${af.keyword}"` : af.selectedValues.join(', ')
          const mode =
            af.mode === 'is'
              ? 'is'
              : af.mode === 'is-not'
                ? 'is not'
                : af.mode === 'includes'
                  ? 'includes'
                  : 'excludes'
          answerLabels.push(`${field.name} ${mode} ${value}`)
        }
      }

      if (loading) {
        entries.push({
          key: catId,
          label: cat.label,
          icon: cat.icon ?? 'AccountTree',
          loading: true,
        })
      } else {
        const parts: string[] = []
        if (processLabels.length) parts.push(`${cat.label}: ${processLabels.join(', ')}`)
        if (stageLabels.length) parts.push(`Stage: ${stageLabels.join(', ')}`)
        if (answerLabels.length) parts.push(answerLabels.join(', '))

        if (parts.length > 0) {
          entries.push({
            key: catId,
            label: parts.join(' · '),
            icon: cat.icon ?? 'AccountTree',
            categoryLabel: cat.label,
            values: [...processLabels, ...stageLabels, ...answerLabels],
          })
        }
      }
    } else {
      const labels: string[] = []
      const tooltips: string[] = []
      for (const oid of optionIds) {
        const label = resolveLabel(oid)
        if (label === null) {
          loading = true
        } else {
          labels.push(label)
        }
        const tooltip = resolveTooltip(oid)
        if (tooltip) tooltips.push(tooltip)
      }
      if (loading) {
        entries.push({
          key: catId,
          label: cat.label,
          icon: cat.icon,
          loading: true,
        })
      } else {
        entries.push({
          key: catId,
          label: `${cat.label}: ${labels.join(', ')}`,
          icon: cat.icon,
          categoryLabel: cat.label,
          values: labels,
          tooltip: tooltips.length > 0 ? tooltips.join(', ') : undefined,
        })
      }
    }
  }

  for (const cat of categories.filter((c) => c.type === 'date')) {
    const range = dateRanges[cat.id]
    const overdueIds = selections[cat.id] ?? []
    const hasRange = range?.from || range?.to
    const hasOverdue = overdueIds.length > 0

    if (!hasRange && !hasOverdue) continue

    const parts: string[] = []
    if (hasOverdue) {
      parts.push(
        overdueIds
          .map((id) => (cat.overdueOption?.id === id ? cat.overdueOption.label : id))
          .join(', '),
      )
    }
    if (hasRange) {
      const from = range!.from?.format('MMM D, YYYY') ?? '…'
      const to = range!.to?.format('MMM D, YYYY') ?? '…'
      parts.push(`${from} – ${to}`)
    }

    entries.push({
      key: cat.id,
      label: `${cat.label}: ${parts.join(', ')}`,
      categoryLabel: cat.label,
      values: parts,
    })
  }

  return entries
}

// ---------------------------------------------------------------------------
// applyFilters
// ---------------------------------------------------------------------------

/**
 * Describes a single filterable row.  Each field is optional — only the fields
 * present in the data need to be supplied.  Keys on `fields` must match the
 * category ids used in `FilterCategory`.
 */
export interface FilterableRow {
  fields: Record<string, string | null>
  arrayFields?: Record<string, string[]>
  process?: string
  stage?: string
  answers?: Record<string, string>
  dates?: Record<string, string | null>
}

export interface ApplyFiltersOptions extends FilterStateOptions {
  rows: FilterableRow[]
  noneOptionCategories?: Record<string, string>
}

const matchesDateRange = (value: string | null, range?: DateRange): boolean => {
  if (!range?.from && !range?.to) return true
  if (!value) return false
  const d = dayjs(value)
  if (range.from && d.isBefore(range.from, 'day')) return false
  if (range.to && d.isAfter(range.to, 'day')) return false
  return true
}

export const applyFilters = ({
  rows,
  categories,
  state,
  stagesByProcess = {},
  formFieldsByProcess = {},
  noneOptionCategories = {},
}: ApplyFiltersOptions): FilterableRow[] => {
  const { selections = {}, dateRanges = {}, refinements = {}, answerFilters = {} } = state
  const processCatId = categories.find((c) => c.type === 'process')?.id

  return rows.filter((row) => {
    for (const [catId, ids] of Object.entries(selections)) {
      if (!ids.length) continue
      const cat = categories.find((c) => c.id === catId)
      if (!cat || cat.type === 'date' || cat.type === 'process') continue

      const noneId = noneOptionCategories[catId]
      if (noneId) {
        const wantsNone = ids.includes(noneId)
        const realIds = ids.filter((id) => id !== noneId)
        const value = row.fields[catId]
        const arrayValue = row.arrayFields?.[catId]

        if (arrayValue) {
          const matchesReal = realIds.length ? realIds.some((f) => arrayValue.includes(f)) : false
          if (!matchesReal && !(wantsNone && arrayValue.length === 0)) return false
        } else if (value === null || value === undefined) {
          if (!wantsNone) return false
        } else {
          if (!realIds.includes(value)) return false
        }
      } else {
        const value = row.fields[catId]
        const arrayValue = row.arrayFields?.[catId]
        if (arrayValue) {
          if (!ids.some((f) => arrayValue.includes(f))) return false
        } else {
          if (value === null || value === undefined) return false
          if (!ids.includes(value)) return false
        }
      }
    }
    if (processCatId) {
      const processIds = selections[processCatId]
      if (processIds?.length && row.process && !processIds.includes(row.process)) return false
    }

    if (row.process && row.stage && processCatId && refinements[processCatId]) {
      for (const [processId, optRef] of Object.entries(refinements[processCatId])) {
        if (row.process !== processId) continue
        const stageIds = optRef['stages'] ?? []
        if (!stageIds.length) continue
        const stages = stagesByProcess[processId] ?? []
        const names = stageIds.map((sid) => stages.find((s) => s.id === sid)?.name).filter(Boolean)
        if (names.length && !names.includes(row.stage)) return false
      }
    }

    if (row.process && row.answers && processCatId && answerFilters[processCatId]) {
      for (const [processId, entries] of Object.entries(answerFilters[processCatId])) {
        if (row.process !== processId) continue
        const applied = entries.filter((f) => f.applied && f.fieldId)
        for (const af of applied) {
          const field = formFieldsByProcess[processId]?.find((ff) => ff.id === af.fieldId)
          if (!field) continue
          const rowValue = row.answers[field.name] ?? ''
          const isText = field.style === 'RawInputStyle' || field.style === 'LongTextInputStyle'
          if (isText) {
            const kw = af.keyword.toLowerCase()
            if (af.mode === 'includes' && !rowValue.toLowerCase().includes(kw)) return false
            if (af.mode === 'excludes' && rowValue.toLowerCase().includes(kw)) return false
          } else {
            if (
              af.mode === 'is' &&
              af.selectedValues.length &&
              !af.selectedValues.includes(rowValue)
            )
              return false
            if (af.mode === 'is-not' && af.selectedValues.includes(rowValue)) return false
          }
        }
      }
    }

    for (const cat of categories.filter((c) => c.type === 'date')) {
      const range = dateRanges[cat.id]
      const overdueIds = selections[cat.id] ?? []
      const hasRange = range?.from || range?.to
      const hasOverdue = overdueIds.length > 0

      if (!hasRange && !hasOverdue) continue

      const dateValue = row.dates?.[cat.id] ?? null

      if (cat.overdueOption && hasOverdue) {
        // OR logic: passes if in range OR overdue
        const inRange = hasRange ? matchesDateRange(dateValue, range) : false
        const isOverdue = dateValue
          ? dayjs(dateValue).isBefore(dayjs().startOf('day'), 'day')
          : false
        if (!inRange && !isOverdue) return false
      } else {
        if (!matchesDateRange(dateValue, range)) return false
      }
    }

    return true
  })
}
